import { t } from 'i18next';

import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

type BackendError = {
    message?: string;
    details?: {
        message: string;
        statusCode: number;
    };
    lastResponse?: Response;
    hasBackendMessage?: boolean;
};

/**
 * `lastResponse` is set by `call()` for every failed request, whatever shape the body has.
 * `details.statusCode` only covers Nest's default error body, hence the fallback.
 */
export function getHttpStatus(error: unknown): number | undefined {
    const backendError = error as BackendError;
    return backendError?.lastResponse?.status ?? backendError?.details?.statusCode;
}

/**
 * Only 401 means the session is gone. A 403 carries a business sentence the user needs to read
 * ('You are not allowed to create order for this customer'), so telling them to refresh the page
 * would both be false and hide that sentence.
 */
export function isSessionExpiredError(error: unknown): boolean {
    return getHttpStatus(error) === 401;
}

/**
 * Some 4xx answers carry a code instead of a sentence ('shopify-instance-not-found',
 * 'Forbidden'), which must never reach a toast. A backend sentence always has a space.
 */
function looksLikeErrorCode(message: string): boolean {
    return !/\s/.test(message);
}

/**
 * The sentence to show the user for a failed request.
 *
 * A 4xx message describes what the caller did and is meant to be read by them. A 5xx message is
 * an internal diagnostic ('Internal error: found no matching warehouse_id'), so it stays behind a
 * generic wording. `t()` is called with the English sentence as key: i18next returns the key
 * itself while the backend catalogue is not imported yet, and starts translating the day it is,
 * without touching a single call site.
 */
export function getDisplayableErrorMessage(error: unknown): string {
    const backendError = error as BackendError;
    const status = getHttpStatus(error);
    const message = backendError?.message;

    if (!backendError?.hasBackendMessage || !message || !status || status >= 500 || looksLikeErrorCode(message)) {
        return t('Internal error!');
    }

    return t(message);
}

export function handleError(error: unknown) {
    const backendError = error as BackendError;
    if (isSessionExpiredError(error)) {
        toast.error(t('Expired session. Please refresh this page'));
    } else {
        toast.error(getDisplayableErrorMessage(error));
    }
    console.error(backendError);
}
